import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Sparkles, ChevronLeft, ShoppingCart, Loader2, Download, Library, Settings, PlayCircle, X, Pencil, Plus, Video, Image as ImageIcon } from "lucide-react";
import { SOLUTIONS, COREX_ENTRY } from "../lib/constants";
import { VideoModal } from "../components/VideoModal";
import { MediaAdminModal } from "../components/MediaAdminModal";

const COREX_LOGO = "/corex-logo-azul.png";
import { fetchCatalogo } from "../lib/catalogo";
import { comprarProducto, descargarProducto } from "../lib/software";
import { humanizeAxiosError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PrecioDescuento } from "../components/PrecioDescuento";
import { PlanBadge } from "../components/PlanBadge";
import { ProgramasSoftware } from "../components/software/ProgramasSoftware";
// PASO 1 · Ocultamiento controlado: la sección "Núcleos Tecnológicos" queda oculta
// temporalmente (no eliminada). Import conservado en comentario para reactivar sin refactor.
// import { NucleosTecnologicos } from "../components/software/NucleosTecnologicos";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const PLAN_LABEL = { pro: "Pro", elite: "Premium" };
const DEMO_VIDEO = "https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4";

function CatalogoPilar({ pilar, color, isAdmin }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [videoItem, setVideoItem] = useState(null);
  const [mediaCtx, setMediaCtx] = useState(null); // { item, modo }

  const cargar = useCallback(() => {
    setLoading(true);
    fetchCatalogo(pilar).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [pilar]);

  useEffect(() => { cargar(); }, [cargar]);

  const esSoftware = pilar === "software";
  const esAcademia = pilar === "academia";

  async function handleAccion(it) {
    if (it.comprado) {
      try {
        await descargarProducto(it.id);
        toast.success("Generando tu descarga segura…");
      } catch (e) { toast.error(humanizeAxiosError(e)); }
      return;
    }
    if (!esSoftware) {
      toast.info("Compra demo — el checkout de este pilar estará disponible pronto.");
      return;
    }
    setBusy(it.id);
    try {
      const r = await comprarProducto(it.id);
      if (r.url) { window.location.href = r.url; return; }
      if (r.comprado) {
        toast.success(r.gratis ? "¡Producto añadido a tu biblioteca!" : "¡Ya lo tienes en tu biblioteca!");
        cargar();
      }
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <div className="mt-10 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando catálogo…</div>;
  if (!data) return null;

  return (
    <section id={`catalogo-${pilar}`} className="mt-10 scroll-mt-24" data-testid={`catalogo-${pilar}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">{data.pilar === "software" ? "Herramientas Técnicas Digitales" : "Tienda de cursos"}</h2>
          <p className="text-sm text-slate-500 mt-0.5">Precios exclusivos para la Familia ConstruLink.</p>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          {isAdmin && esSoftware && (
            <button
              onClick={() => navigate("/admin/software?nuevo=1")}
              data-testid="catalogo-agregar-btn"
              className="inline-flex items-center gap-1.5 rounded-xl border border-brand-navy bg-brand-navy px-3.5 py-2 text-sm font-semibold text-white hover:bg-brand-blue transition-colors"
            >
              <Plus size={15} /> Agregar otro programa o plantilla
            </button>
          )}
          {esSoftware && (
            <Link to="/software/mis-descargas" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="catalogo-mis-descargas">
              <Library size={15} /> Mis Descargas
            </Link>
          )}
          {pilar === "academia" && (
            <Link to="/academia/mis-cursos" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="catalogo-mis-cursos">
              <Library size={15} /> Mis Cursos
            </Link>
          )}
          {data.descuento_pct > 0 ? (
            <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2" data-testid="catalogo-banner-ahorro">
              <PlanBadge plan={data.plan} />
              <span className="text-sm font-semibold text-emerald-700">Estás ahorrando {data.descuento_pct}% con tu membresía</span>
            </div>
          ) : (
            <Link to="/planes" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-sm font-semibold text-brand-blue hover:bg-slate-50" data-testid="catalogo-banner-upsell">
              Hazte Pro y ahorra hasta 30% <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
        {data.items.map((it) => (
          <div key={it.id} className="relative bg-white rounded-2xl border border-slate-200 shadow-soft p-5 flex flex-col overflow-hidden transition-shadow hover:shadow-lg" data-testid={`catalogo-item-${it.id}`}>
            {isAdmin && esSoftware ? (
              <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5" data-testid={`catalogo-acciones-${it.id}`}>
                <button type="button" onClick={() => setMediaCtx({ item: it, modo: "video" })} data-testid={`catalogo-video-admin-${it.id}`} aria-label="Video"
                  className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800">
                  <Video size={14} />
                </button>
                <button type="button" onClick={() => setMediaCtx({ item: it, modo: "imagen" })} data-testid={`catalogo-imagen-admin-${it.id}`} aria-label="Imagen"
                  className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800">
                  <ImageIcon size={14} />
                </button>
                <button type="button" onClick={() => navigate(`/admin/software?edit=${it.id}`)} data-testid={`catalogo-editar-icon-${it.id}`} aria-label="Editar"
                  className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800">
                  <Pencil size={14} />
                </button>
              </div>
            ) : (
              esSoftware && it.tiene_video_demo && (
                <button type="button" onClick={() => setVideoItem(it)} data-testid={`catalogo-video-${it.id}`} aria-label="Ver video"
                  className="absolute right-3 top-3 z-10 rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50 hover:text-brand-blue">
                  <Video size={14} />
                </button>
              )
            )}
            {it.tiene_imagen && (
              <img src={`${BACKEND}/api/software/${it.id}/imagen`} alt={it.nombre} className="-mx-5 -mt-5 mb-3 h-28 w-[calc(100%+2.5rem)] object-cover" loading="lazy" />
            )}
            <span className="text-[10px] font-bold uppercase tracking-wider self-start px-2 py-1 rounded-full" style={{ color, backgroundColor: `${color}1a` }}>{it.categoria}</span>
            <h3 className="mt-2 text-sm font-bold text-slate-900 leading-snug line-clamp-2">{it.nombre}</h3>
            <p className="mt-1 text-xs text-slate-600 leading-relaxed flex-1 line-clamp-3">{it.descripcion}</p>
            <div className="mt-4 flex flex-col items-stretch gap-2">
              <div className="flex flex-col items-start gap-1">
                <PrecioDescuento original={it.precio_original} final={it.precio_final} descuentoPct={data.descuento_pct} />
                {data.descuento_pct > 0 && it.ahorro > 0 ? (
                  <span className="text-[11px] font-semibold text-emerald-600" data-testid={`catalogo-ahorro-${it.id}`}>
                    Ahorraste S/ {Number(it.ahorro).toFixed(2)}
                  </span>
                ) : (
                  <Link to="/planes" data-testid={`catalogo-upsell-${it.id}`} className="text-[11px] font-semibold text-brand-blue hover:underline">
                    Ahorra hasta 30% con Premium
                  </Link>
                )}
              </div>
              {isAdmin && esSoftware ? null : esAcademia ? (
                <Link
                  to={`/academia/curso/${it.id}`}
                  data-testid={`catalogo-comprar-${it.id}`}
                  className="inline-flex items-center justify-center gap-2 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-opacity hover:opacity-90"
                  style={{ backgroundColor: it.comprado ? "#16A34A" : color }}
                >
                  {it.comprado ? <PlayCircle size={15} /> : <ShoppingCart size={15} />}
                  {it.comprado ? "Ver curso" : "Ver curso"}
                </Link>
              ) : (
                <button
                  onClick={() => handleAccion(it)}
                  disabled={busy === it.id}
                  data-testid={`catalogo-comprar-${it.id}`}
                  className="inline-flex items-center justify-center gap-2 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-opacity hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: it.comprado ? "#16A34A" : color }}
                >
                  {busy === it.id ? <Loader2 size={15} className="animate-spin" /> : (it.comprado ? <Download size={15} /> : <ShoppingCart size={15} />)}
                  {it.comprado ? "Descargar" : "Comprar"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {mediaCtx && (
        <MediaAdminModal
          open={!!mediaCtx}
          modo={mediaCtx.modo}
          tipo="producto"
          item={mediaCtx.item}
          onClose={() => setMediaCtx(null)}
          onSaved={() => { setMediaCtx(null); cargar(); }}
        />
      )}
      <VideoModal
        open={!!videoItem}
        onClose={() => setVideoItem(null)}
        title={videoItem?.nombre}
        externalUrl={videoItem?.video_demo_url || null}
        fileUrl={videoItem && !videoItem.video_demo_url ? `${BACKEND}/api/software/${videoItem.id}/video-demo` : null}
      />
    </section>
  );
}

export default function SolutionPage({ solutionId }) {
  const s = SOLUTIONS.find((x) => x.id === solutionId);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showDemo, setShowDemo] = useState(false);
  if (!s) return null;
  if (s.proximamente) {
    return (
      <div className="px-5 md:px-8 py-8" data-testid={`solution-page-${s.id}`}>
        <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
          <ChevronLeft size={16} /> Volver al Ecosistema
        </Link>
        <section
          className="mt-4 relative rounded-3xl overflow-hidden border border-slate-100 shadow-soft bg-white p-8 md:p-12 text-center"
          style={{ background: `linear-gradient(135deg, #FFFFFF 55%, ${s.tint} 100%)` }}
          data-testid="solution-proximamente"
        >
          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full text-white" style={{ backgroundColor: s.color }}>
            <Sparkles size={13} /> Próximamente
          </span>
          <img src={s.iconImage} alt={s.title} className="mx-auto mt-6 h-40 md:h-52 object-contain drop-shadow-2xl" />
          <h1 className="mt-6 text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">{s.tagline}</h1>
          <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">
            {s.description} Estamos afinando esta solución para ofrecerte la mejor experiencia. ¡Muy pronto disponible!
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-xl mx-auto">
            {s.features.map((f) => (
              <span key={f} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/70 border border-slate-100 text-xs font-semibold text-slate-600">
                <CheckCircle2 size={12} style={{ color: s.color }} /> {f}
              </span>
            ))}
          </div>
          <Link to="/" data-testid="proximamente-volver" className="mt-8 inline-flex items-center gap-2 text-white font-semibold px-6 py-3 rounded-xl shadow-sm hover:brightness-110 transition-all" style={{ backgroundColor: s.color }}>
            Explorar otras soluciones <ArrowRight size={16} />
          </Link>
        </section>
      </div>
    );
  }
  const tieneCatalogo = solutionId === "software" || solutionId === "academia";
  const esAdminSoftware = solutionId === "software" && user?.role === "admin";
  const esAdminAcademia = solutionId === "academia" && user?.role === "admin";

  const explorarModulo = () => {
    const el = document.getElementById(tieneCatalogo ? `catalogo-${solutionId}` : `features-${s.id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const stats = [
    { label: "Módulos activos", value: "6" },
    { label: "Beta users", value: "1,240" },
    { label: "Uptime", value: "99.9%" },
  ];

  return (
    <div className="px-5 md:px-8 py-8" data-testid={`solution-page-${s.id}`}>
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Ecosistema
      </Link>

      <section
        className="mt-4 relative rounded-3xl overflow-hidden border border-slate-100 shadow-soft bg-white p-8 md:p-10"
        style={{
          background: `linear-gradient(135deg, #FFFFFF 55%, ${s.tint} 100%)`,
        }}
      >
        <div className="flex items-center justify-between gap-8 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-3">
              <span
                className="text-white font-black text-xs px-3 py-1.5 rounded-lg shadow-sm"
                style={{ backgroundColor: s.color }}
              >
                {s.number}
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: s.color }}>
                Solución {s.number} · Ecosistema ConstruLink
              </span>
            </div>
            <h1 className="mt-3 text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              {s.tagline}
            </h1>
            <p className="mt-3 text-base text-slate-600 max-w-2xl">
              {s.description} Prepárate para experimentar la nueva forma de trabajar en la industria.
            </p>
            <div className="mt-6 flex gap-3 flex-wrap">
              {esAdminSoftware && (
                <Link
                  to="/admin/software"
                  data-testid="software-gestionar-btn"
                  className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm transition-colors"
                >
                  <Settings size={16} /> Gestionar productos
                </Link>
              )}
              {esAdminAcademia && (
                <Link
                  to="/admin/academia"
                  data-testid="academia-gestionar-btn"
                  className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm transition-colors"
                >
                  <Settings size={16} /> Gestionar cursos
                </Link>
              )}
              <button
                onClick={explorarModulo}
                data-testid={`solution-primary-cta-${s.id}`}
                className="inline-flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm hover:brightness-110 transition-all"
                style={{ backgroundColor: s.color }}
              >
                Explorar módulo <ArrowRight size={16} />
              </button>
              <button
                onClick={() => setShowDemo(true)}
                data-testid="solution-ver-demo-btn"
                className="inline-flex items-center gap-2 border border-slate-200 hover:border-slate-300 hover:bg-white text-slate-700 font-semibold px-5 py-2.5 rounded-xl transition-colors"
              >
                <Sparkles size={16} /> Ver demo
              </button>
            </div>
          </div>
          {solutionId === "software" && (
            <div className="relative flex-1 min-w-[240px] max-w-sm self-center flex flex-col items-center gap-5" data-testid="hero-corex-center">
              <div
                aria-hidden
                className="absolute inset-6 rounded-full blur-3xl opacity-40"
                style={{ background: `radial-gradient(circle at 50% 45%, ${s.color}66, transparent 70%)` }}
              />
              <img
                src={COREX_LOGO}
                alt="COREX · Núcleo de Ingeniería"
                className="relative w-full h-40 md:h-48 object-contain drop-shadow-2xl animate-fade-up"
                data-testid="hero-corex-logo"
              />
              <button
                type="button"
                onClick={() => {
                  try {
                    window.localStorage.removeItem("cl_corex_autoopen");
                    window.localStorage.removeItem("cl_corex_kiosk");
                    window.localStorage.setItem("cl_corex_origin", "independent");
                  } catch (e) { /* noop */ }
                  navigate(COREX_ENTRY);
                }}
                data-testid="hero-abrir-corex-btn"
                className="relative inline-flex items-center gap-2 rounded-xl px-8 py-3.5 text-base font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                style={{ background: "linear-gradient(135deg,#1D4ED8,#0EA5B7)" }}
              >
                Abrir COREX <ArrowRight size={18} aria-hidden="true" />
              </button>
            </div>
          )}
          <div className="relative flex-1 min-w-[260px] max-w-md self-center">
            <div
              aria-hidden
              className="absolute inset-6 rounded-full blur-3xl opacity-40"
              style={{ background: `radial-gradient(circle at 50% 45%, ${s.color}66, transparent 70%)` }}
            />
            <img
              src={s.iconImage}
              alt={s.title}
              className="relative w-full h-56 md:h-72 object-contain drop-shadow-2xl animate-fade-up"
              data-testid={`solution-hero-image-${s.id}`}
            />
          </div>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
          {stats.map((st) => (
            <div key={st.label} className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-100 p-3 text-center">
              <p className="text-lg font-extrabold" style={{ color: s.color }}>
                {st.value}
              </p>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                {st.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* PASO 1 · Sección "Núcleos Tecnológicos" OCULTA temporalmente (no eliminada).
          Rutas, páginas y componente (NucleosTecnologicos.jsx) se conservan intactos.
      {solutionId === "software" && <NucleosTecnologicos isAdmin={esAdminSoftware} />} */}

      {solutionId === "software" && <ProgramasSoftware isAdmin={esAdminSoftware} />}

      {tieneCatalogo && <CatalogoPilar pilar={solutionId} color={s.color} isAdmin={esAdminSoftware} />}

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6" id={`features-${s.id}`}>
        {s.features.map((f, i) => (
          <div key={f} className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6" data-testid={`solution-feature-${s.id}-${i}`}>
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
              style={{ backgroundColor: s.tint, color: s.color }}
            >
              <CheckCircle2 size={20} />
            </div>
            <h3 className="text-base font-bold text-slate-900">{f}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Funcionalidad integrada en el Ecosistema ConstruLink. Compatible con las otras 4 soluciones bajo una sola identidad digital.
            </p>
          </div>
        ))}
      </section>

      <section className="mt-10 rounded-2xl border border-slate-100 shadow-soft bg-white p-6 lg:p-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">
          Preparado para integrar
        </p>
        <h3 className="mt-2 text-xl font-extrabold text-slate-900">
          Este módulo comparte identidad con las otras 4 soluciones
        </h3>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          Base de datos centralizada · Mensajería integrada · IA transversal · Roles y permisos compartidos · Pagos centralizados.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {SOLUTIONS.filter((x) => x.id !== s.id).map((x) => (
            <Link
              key={x.id}
              to={x.path}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-slate-100 border border-slate-100 text-xs font-semibold text-slate-700"
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: x.color }} />
              {x.title}
            </Link>
          ))}
        </div>
      </section>

      {showDemo && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
          data-testid="demo-modal"
          onClick={() => setShowDemo(false)}
        >
          <div className="w-full max-w-3xl bg-slate-900 rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <p className="text-white font-semibold text-sm">Demo · {s.title}</p>
              <button onClick={() => setShowDemo(false)} data-testid="demo-close-btn" className="text-white/60 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>
            <video src={DEMO_VIDEO} controls autoPlay muted className="w-full aspect-video bg-black" data-testid="demo-video" />
          </div>
        </div>
      )}
    </div>
  );
}
